import React, { useEffect, useState } from 'react';
import Modal from '../../common/ModalPopup';
import DatePicker from "react-datepicker";
import ReactSelect from "@common/ReactSelect";
import ContractService from '../../../src/services/ContractService';
import moment from 'moment';
import { useLanguage } from '../../../src/context/LanguageContext';


const ContractPopup: React.FC<any> = ({ showContractPopup, handleModalClose, selectedPartner, contractStatusList, paymentTypeList, from, selectedAgency, partnerList, handleLoading }) => {
    const [tableData, setTableData] = useState<any>([]);
    const [editingRow, setEditingRow] = useState<number | null>(null);
    const [editData, setEditData] = useState<any>({});
    const [partners, setPartners] = useState<any>([]);
    const [partnerId, setPartnerId] = useState<number | null>(null);
    const [errors, setErrors] = useState<any>({});
    const [showAddButton, setShowAddButton] = useState<boolean>(true);
    const {translations} = useLanguage();

    useEffect(() => {
        setErrors({});
        if (showContractPopup) {
            getContractListing();
        }

        if (from === "partnerList") {
            setPartnerId(selectedPartner.id);
        }

        if (selectedAgency && from === "agencyList") {
            const filteredPartners = partnerList
                .filter((partner: any) => selectedAgency?.partner_map?.includes(partner.label))
                .map((partner: any) => ({
                    value: partner.value,
                    label: partner.label
                }));

            setPartners(filteredPartners);
        }
    // eslint-disable-next-line
    }, [showContractPopup]);


    const startEditing = (row: any) => {
        if (from === "agencyList") setPartnerId(null);
        setEditingRow(row.id);
        setEditData({ ...row });
    };

    const cancelEditing = () => {
        setEditingRow(null);
        getContractListing();
        setErrors({});
    };

    const isFormValid = (data: any) => {
        let isValid = true;

        let updatedError: any = {}
        //Start Date validation
        if (!data.start_date) {
            updatedError['start_date'] = 'Start date is required';
            isValid = false;
        }

        // End Date validation
        if (!data.end_date) {
            updatedError['end_date'] = 'End date is required';
            isValid = false;
        }

        // Insurer validation for agency
        if (from === "agencyList" && !data.partner_id) {
            updatedError['partner_id'] = 'Insurer is required';
            isValid = false;
        }

        // Payment Account validation
        if (!data.payment_type) {
            updatedError['payment_type'] = 'Payment type is required';
            isValid = false;
        }

        setErrors({ ...updatedError });

        return isValid;
    }

    const saveEdit = async (rowId: any = null) => {
        try {
            if (!isFormValid(editData)) {
                return;

            }
            // eslint-disable-next-line 
            const params = {
                ...editData,
                id: Number(rowId),
                payment_type: editData.payment_type,
                remark: editData.remark || "",
                status: editData.status,
                type: (from === "agencyList") ? 2 : 1,
                partner_id: (from === "agencyList") ? Number(editData.partner_id) : Number(selectedPartner.id),
                agency_id: (from === "agencyList") ? Number(selectedAgency.id) : null
            };
            ['created_at', 'updated_at'].forEach((key:any) => delete params[key]);
            await ContractService.saveContract(params);
            setEditingRow(null);
            getContractListing();
        } catch (error) {
            console.error("Error saving contract:", error);
        }

    };

    const disableRow = async (row: any) => {
        try {
            const updatedData = { ...row, status: 0 };
            ['created_at', 'updated_at'].forEach((key:any) => delete updatedData[key]);
            await ContractService.saveContract(updatedData);
            getContractListing();
        } catch (error) {
            console.error("Error saving contract:", error);
        }
    };

    const addNewRow = () => {
        const newRow = {
            start_date: '',
            end_date: '',
            payment_type: '',
            remark: '',
            status: 2
        };
        setTableData((prev: any) => [...prev, newRow]);
        startEditing(newRow);
        setShowAddButton(false);
    };

    const handleInputChange = (field: any, value: any) => {
        if (errors[field]) {
            setErrors((prev: any) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
        if (field === "partner_id") {
            setPartnerId(value.value);
            setEditData({});
        }

        if (["start_date", "end_date"].includes(field)) {
            setEditData((prev: any) => ({
                ...prev,
                [field]: value ? moment(value).format('YYYY-MM-DD') : null
            }));
        } else if (['payment_type', 'status', 'partner_id'].includes(field)) {
            setEditData((prev: any) => ({ ...prev, [field]: value.value }));
        } else {
            setEditData((prev: any) => ({ ...prev, [field]: value }));
        }

    };

    const getContractListing = async () => {
        handleLoading(true);
        //API call to fetch contract listing based on selectedPartner or selectedAgency
        try {
            let filters = {
                ...(from === "agencyList"
                    ? { agency_id: selectedAgency.id }
                    : { partner_id: selectedPartner.id }
                ),
                type: (from === 'agencyList') ? 2 : 1
            };
            const value = await ContractService.getContractListing(filters);
            const data = value?.data?.data || [];
            setTableData(data);
            handleLoading(false);
            setShowAddButton(true);
        } catch (error) {
            handleLoading(false);
        }
    }

    const getPreviousEndDate = (partner_id?: number, currentId?: number) => {
        const pid = partner_id ?? partnerId;

        const contracts = tableData
            .filter((c: any) => c.partner_id === pid && c.id !== currentId && ![0].includes(c.status))
            .sort((a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
                    
        const current = tableData.find((c: any) => c.id === currentId);

        if (!current || !current?.start_date) {
            return contracts.length > 0 ? contracts[contracts.length - 1].end_date : null;
        }

        const value = contracts
            .filter((c: any) => new Date(c.end_date) <= new Date(current.start_date))
            .pop()?.end_date || null;

        return value;
    };

    const getNextStartDate = (partner_id?: number, currentId?: number) => {
        const pid = partner_id ?? partnerId;

        const contracts = tableData
            .filter((c: any) => c.partner_id === pid && c.id !== currentId && ![0].includes(c.status))
            .sort((a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

        const current = tableData.find((c: any) => c.id === currentId);
        if (!current) return null;

        return contracts.find((c: any) => new Date(c.start_date) >= new Date(current.end_date))?.start_date || null;
    };

    return (
        <div className="contract-popup">
            <Modal show={showContractPopup ?? false} handleClose={handleModalClose} >
                <div className="modal-header">
                </div>

                <div className="modal-body" >
                    <h2>
                        {
                            (from === "partnerList") ?
                                (`${selectedPartner?.name} ${selectedPartner?.id ? `(ID: ${selectedPartner?.id})` : ""}`)
                                : (`${selectedAgency?.name} ${selectedAgency?.id ? `(ID: ${selectedAgency?.id})` : ""}`)
                        }
                    </h2>
                    <table className={(from === "partnerList") ? "sm-contract" : ""}>
                        <thead>
                            <tr>
                                {from === "agencyList" && <th>Insurer</th>}
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Payment Account</th>
                                <th>Remarks</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(tableData && tableData.length > 0) ? tableData.map((row: any) => (
                                <tr key={row.id} className={(row.status === 0) ? "disabled-row" : ""}>
                                    {
                                        from === "agencyList" && <td>

                                            {editingRow === row.id ? (
                                                <>
                                                    <ReactSelect
                                                        options={partners}
                                                        value={partners?.find((item: any) => +item.value === +editData.partner_id) || ''}
                                                        handleChange={(e: any) => handleInputChange('partner_id', e)}
                                                        placeholder={""}
                                                        fieldKey=""
                                                        disableMargin={true}
                                                        menuPlacement={"top"}
                                                    />
                                                    {
                                                        errors[`partner_id`]
                                                            ?
                                                            <span className="error-txt error-contract">{errors[`partner_id`]}</span>
                                                            :
                                                            ''
                                                    }
                                                </>

                                            ) : (
                                                partners?.find((item: any) => item.value === row.partner_id)?.label || '-'
                                            )}
                                        </td>}
                                    <td>
                                        {editingRow === row.id ? (
                                            <>
                                                <div className="datepicker">
                                                    <DatePicker
                                                        selected={editData.start_date ? new Date(editData.start_date) : null}
                                                        onChange={(date) => handleInputChange('start_date', date)}
                                                        dateFormat={"yyyy/MM/dd"}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        placeholderText=""
                                                        minDate={
                                                            getPreviousEndDate(row.partner_id, row.id)
                                                                ? new Date(new Date(getPreviousEndDate(row.partner_id, row.id)).getTime() + 24 * 60 * 60 * 1000)
                                                                : undefined
                                                        }
                                                        maxDate={
                                                            getNextStartDate(row.partner_id, row.id)
                                                                ? new Date(new Date(getNextStartDate(row.partner_id, row.id)).getTime() - 24 * 60 * 60 * 1000)
                                                                : undefined
                                                        }
                                                    />
                                                    <label title={''} data-label={'Start Date'} className={`form-label datepick-heading`}></label>

                                                </div>
                                                {
                                                    errors[`start_date`]
                                                        ?
                                                        <span className="error-txt error-contract">{errors[`start_date`]}</span>
                                                        :

                                                        ''
                                                }
                                            </>

                                        ) : (
                                            row.start_date || '-'
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === row.id ? (
                                            <>
                                                <div className="datepicker">
                                                    <DatePicker
                                                        selected={editData.end_date ? new Date(editData.end_date) : null}
                                                        onChange={(date) => handleInputChange('end_date', date)}
                                                        dateFormat={"yyyy/MM/dd"}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        placeholderText=""
                                                        minDate={editData.start_date ? new Date(new Date(editData.start_date).getTime() + 24 * 60 * 60 * 1000) : undefined}
                                                        maxDate={
                                                            getNextStartDate(row.partner_id, row.id)
                                                                ? new Date(new Date(getNextStartDate(row.partner_id, row.id)).getTime() - 24 * 60 * 60 * 1000)
                                                                : undefined
                                                        }
                                                    />
                                                    <label title={''} data-label={'End Date'} className={`form-label datepick-heading`}></label>

                                                </div>
                                                {
                                                    errors[`end_date`]
                                                        ?
                                                        <span className="error-txt error-contract">{errors[`end_date`]}</span>
                                                        :
                                                        ''
                                                }
                                            </>
                                        ) : (
                                            row.end_date || '-'
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === row.id ? (
                                            <>
                                                <ReactSelect
                                                    options={paymentTypeList}
                                                    value={paymentTypeList?.find((item: any) => +item?.value === +editData?.payment_type) || ''}
                                                    handleChange={(e: any) => handleInputChange('payment_type', e)}
                                                    placeholder={""}
                                                    fieldKey=""
                                                    disableMargin={true}
                                                    menuPlacement={'top'}
                                                />
                                                {
                                                    errors[`payment_type`]
                                                        ?
                                                        <span className="error-txt error-contract">{errors[`payment_type`]}</span>
                                                        :
                                                        ''
                                                }
                                            </>

                                        ) : (
                                            paymentTypeList?.find((item: any) => item.value === row.payment_type)?.label || '-'
                                        )}

                                    </td>
                                    <td>
                                        {editingRow === row.id ? (
                                            // <textarea
                                            //     value={editData.remark}
                                            //     onChange={(e) => handleInputChange('remark', e.target.value)}
                                            //     rows={2}
                                            // />
                                            <fieldset className="">
                                                <div className="material">
                                                    <textarea id="remark" rows={1} placeholder=" " name="remark" className="form-input textarea-height" value={editData.remark} onChange={(e) => handleInputChange('remark', e.target.value)} />
                                                </div>
                                            </fieldset>
                                        ) : (
                                            row?.remark || '-'
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === row.id ? (
                                            <ReactSelect
                                                options={contractStatusList}
                                                value={contractStatusList?.find((item: any) => item.value === editData.status) || ''}
                                                handleChange={(e: any) => handleInputChange('status', e)}
                                                placeholder={""}
                                                fieldKey=""
                                                disableMargin={true}
                                                menuPlacement={'top'}
                                            />
                                        ) : (
                                            contractStatusList?.find((item: any) => item.value === row.status)?.label || 'Inactive'
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === row.id ? (
                                            <>
                                                <ul className="action-btn">
                                                    <li title="Save" onClick={() => saveEdit(row.id)}>
                                                        <i className="ic-check"></i>
                                                    </li>
                                                    <li title="Cancel" onClick={cancelEditing}>
                                                        <i className="ic-clearclose"></i>
                                                    </li>
                                                </ul>
                                            </>
                                        ) : (
                                            <>
                                                <ul className="action-btn">
                                                    <li title="Edit" onClick={[0, 3].includes(row.status) ? undefined : () => startEditing(row)}
                                                        className={[0, 3].includes(row.status) ? 'disabled' : ''}>
                                                        <i className="ic-edit"></i>
                                                    </li>
                                                    <li title="Disable" onClick={() => disableRow(row)} className={[0].includes(row.status) ? 'disabled' : ''}>
                                                        <i className="ic-peson-blacklist"></i>
                                                    </li>
                                                </ul>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )) : <tr><td colSpan={from === "agencyList" ? 7 : 6} style={{ textAlign: 'center' }}>{translations.commonMsg?.['No data found'] || translations.commonMsg?.loading}</td></tr>
                            }
                        </tbody>
                    </table>

                    <div className='add-more-icon'>
                        {   
                            showAddButton && 
                            <span onClick={addNewRow}>
                                <i className='ic-add'></i> Add
                            </span>
                        }
                        
                    </div>
                </div>
            </Modal>
        </div>
    )
};

export default ContractPopup;